import type { AgentType } from 'features/trend-detection/types'

export interface SubmitBlockersInput {
  agentType: AgentType
  isEditMode: boolean
  name: string
  issuePrompt: string
  generalPrompt: string
  benchmarkingEntityType: 'brand' | 'product_hierarchy' | null
  benchmarkingFocusCount: number
}

/** User-facing reasons the create/edit form can't be submitted yet, in
 * display order. Empty means submittable.
 *
 * Edit mode relaxes the benchmarking requirement that create enforces (a
 * non-null entityType with at least one focus-group filter): an existing
 * agent can be legitimately configured to compare against "all other
 * brands" with an empty focus group. The cohort/focus overlap check still
 * gates the submit itself (via a toast), inside
 * `useBenchmarkingUpdateSubmit`. */
export function getSubmitBlockers({
  agentType,
  isEditMode,
  name,
  issuePrompt,
  generalPrompt,
  benchmarkingEntityType,
  benchmarkingFocusCount,
}: SubmitBlockersInput): string[] {
  const blockers: string[] = []
  if (!name.trim()) blockers.push('Enter a name')

  if (agentType === 'benchmarking') {
    if (isEditMode) return blockers
    if (!benchmarkingEntityType) {
      blockers.push('Choose brands or products to benchmark')
    } else if (benchmarkingFocusCount === 0) {
      blockers.push(
        benchmarkingEntityType === 'brand'
          ? 'Add at least one brand to compare'
          : 'Add at least one product to compare',
      )
    }
  } else if (agentType === 'general') {
    if (!generalPrompt.trim()) {
      blockers.push('Describe what this agent should do')
    }
  } else if (!isEditMode && !issuePrompt.trim()) {
    blockers.push('Describe what this agent should monitor')
  }
  return blockers
}
